import React, {
  useCallback, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { useDispatch } from 'react-redux';
import Card from '../../../../components/card/card.component';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import ModalDescription from '../../../../components/modal-description/modal-description.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import FieldText from '../../../../components/field-text/field-text.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import ControlButtonBlock from '../../../app/common/control-button-block/control-button-block.component';
import FormService from '../../../../services/form-service';
import { inviteUser } from '../settings-user-list.action';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const SettingsUserInvitationModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MAX_LENGTH_COMMENT: number = 1000;

  const {
    isOpen,
    isRightDrawerOpen,
    userId,
    userList,
    onCloseModal,
  } = props;

  const dispatch: ReduxDispatch = useDispatch();

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [message, setMessage]: [string, Function] = useState('');

  const menuItemUserList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: userList,
    selectedValue: userId,
    defaultImage: PersonDefaultImage,
  }), [userList, userId]);

  const sendForm: Function = useCallback((): void => {
    if (!userId || !message || !message.trim()) {
      return;
    }

    dispatch(inviteUser({
      invitationForm: {
        userId,
        message: message.trim(),
      },
    }, (): void => {
      setMessage(null);

      setStatusMessage('Invitation was sent successfully');
      setIsOpenMessage(true);

      if (onCloseModal) {
        onCloseModal();
      }
    }));
  }, [userId, message]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const closeModal: Function = useCallback((): void => {
    setMessage(null);

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  return (
    <Fragment>
      <ModalLayout isOpen={isOpen} title="Send Invitation" onCloseModal={closeModal}>
        <Card isContainer withNoPadding>
          <ModalDescription>
            This action will send invitation to the email of specified user with the link to registration form
          </ModalDescription>
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              <TableRow>
                <TableCell>
                  User
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FieldSelect isPlain isBordered isDisabled value={userId}>
                    {menuItemUserList}
                  </FieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Additional Message
                </TableCell>
                <TableCell>
                  <FieldText
                    isPlain
                    isBordered
                    isMultiline
                    minRows={FormService.TEXTAREA_MIN_ROWS_SMALL}
                    maxLength={MAX_LENGTH_COMMENT}
                    onChange={(event: React.ChangeEvent, value: string): void => {
                      setMessage(value);
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        <ControlButtonBlock
          isModal
          isNewEntity
          withOffset={isRightDrawerOpen}
          labelSave="Send"
          onCancelForm={closeModal}
          onSubmitForm={sendForm}
        />
      </ModalLayout>
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default SettingsUserInvitationModal;
